import React, { useState } from 'react';
import { cleanseMonkeyMind } from '../services/geminiService';
import { MindGardenResponse } from '../types';
import { Wind, Trash2, Leaf, ArrowRight } from 'lucide-react';
import { useUser } from '../context/UserContext';

const ZenGardenPage: React.FC = () => {
  const [worry, setWorry] = useState('');
  const [response, setResponse] = useState<MindGardenResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp, unlockBadge } = useUser();

  const handleCleanse = async () => {
    if (!worry.trim()) return;
    setLoading(true);
    setResponse(null);
    try {
      const data = await cleanseMonkeyMind(worry);
      setResponse(data);
      addXp(30, 'Cleansed Monkey Mind');
      unlockBadge('mind_gardener');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setWorry('');
    setResponse(null);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-emerald-50 text-emerald-600 rounded-full mb-4">
          <Wind size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Mind Garden</h1>
        <p className="text-nunchi-600">The "Monkey Mind" is restless and loud. <br/>Release your worries here and watch them dissolve into nature.</p>
      </header>

      {!response && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What is troubling you today?
            </label>
            <textarea
              value={worry}
              onChange={(e) => setWorry(e.target.value)}
              placeholder="I am worried about..."
              className="w-full h-40 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-emerald-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleCleanse}
              disabled={!worry.trim()}
              className="w-full bg-emerald-700 text-white py-4 rounded-xl font-bold hover:bg-emerald-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Wind size={18} /> Release to the Wind
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="flex justify-center gap-2 mb-4">
                 <Leaf className="text-emerald-300 animate-bounce" style={{ animationDelay: '0s'}} />
                 <Leaf className="text-emerald-400 animate-bounce" style={{ animationDelay: '0.2s'}} />
                 <Leaf className="text-emerald-500 animate-bounce" style={{ animationDelay: '0.4s'}} />
             </div>
             <p className="font-serif italic text-emerald-700">The wind is carrying it away...</p>
        </div>
      )}

      {response && (
        <div className="animate-fadeIn">
            <div className="bg-emerald-900 text-emerald-50 p-8 rounded-2xl shadow-xl relative overflow-hidden mb-8">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                 
                 <h3 className="text-emerald-300 uppercase tracking-widest text-xs font-bold mb-4">Perspective Shift</h3>
                 <p className="text-lg leading-relaxed font-serif mb-8 border-l-4 border-emerald-500 pl-4">
                     {response.metaphor}
                 </p>

                 <div className="bg-white/10 p-6 rounded-xl text-center italic font-serif text-xl md:text-2xl">
                     {response.haiku.split('\n').map((line, i) => (
                         <div key={i} className="mb-1">{line}</div>
                     ))}
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={reset}
                  className="text-emerald-600 hover:text-emerald-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <Trash2 size={16} /> Clear Mind Again
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default ZenGardenPage;